/**
 * Error Handling Utilities
 *
 * Centralized error handling for authentication, Firestore, and general operations.
 * Provides French-localized error messages and consistent error handling patterns.
 */

import { toast } from 'sonner';

// ============================================================================
// TYPE DEFINITIONS
// ============================================================================

/**
 * Firebase-style error with code property
 */
export interface FirebaseError extends Error {
    code?: string;
}

/**
 * Zod validation error with issues array
 */
export interface ZodValidationError extends Error {
    issues?: Array<{
        message: string;
        path?: (string | number)[];
        code?: string;
    }>;
}

/**
 * Combined error type that handles various error formats
 */
export type AppError = FirebaseError | ZodValidationError | Error;

/**
 * Options for error handling
 */
export interface ErrorHandleOptions {
    context?: string;
    fallbackMessage?: string;
    showToast?: boolean;
    log?: boolean;
}

/**
 * Options for retry operations
 */
export interface RetryOptions {
    maxRetries?: number;
    baseDelay?: number;
    shouldRetry?: (error: AppError) => boolean;
}

/**
 * Error metadata for logging
 */
export interface ErrorMetadata {
    [key: string]: unknown;
}

// ============================================================================
// ERROR CODES AND MESSAGES
// ============================================================================

/**
 * Firebase Auth error codes to French messages
 */
const AUTH_ERROR_MESSAGES: Record<string, string> = {
    'auth/invalid-email': "L'adresse email n'est pas valide.",
    'auth/user-disabled': "Ce compte a été désactivé.",
    'auth/user-not-found': "Aucun compte trouvé avec cet email.",
    'auth/wrong-password': "Email ou mot de passe incorrect.",
    'auth/invalid-credential': "Email ou mot de passe incorrect.",
    'auth/email-already-in-use': "Cette adresse email est déjà utilisée.",
    'auth/weak-password': "Le mot de passe doit contenir au moins 6 caractères.",
    'auth/operation-not-allowed': "Opération non autorisée.",
    'auth/too-many-requests': "Trop de tentatives. Veuillez réessayer plus tard.",
    'auth/network-request-failed': "Erreur de connexion réseau. Vérifiez votre internet.",
    'auth/api-key-not-valid': "Erreur de configuration: clé API invalide. Contactez l'administrateur.",
    'auth/requires-recent-login': "Cette opération nécessite une reconnexion récente.",
    'auth/popup-closed-by-user': "La fenêtre de connexion a été fermée.",
    'auth/account-exists-with-different-credential': "Un compte existe déjà avec cet email.",
};

/**
 * Firebase Firestore error codes to French messages
 */
const FIRESTORE_ERROR_MESSAGES: Record<string, string> = {
    'permission-denied': "Vous n'avez pas la permission d'effectuer cette action.",
    'not-found': "Document introuvable.",
    'already-exists': "Ce document existe déjà.",
    'resource-exhausted': "Quota dépassé. Veuillez réessayer plus tard.",
    'failed-precondition': "Opération impossible dans l'état actuel.",
    'aborted': "Opération annulée. Veuillez réessayer.",
    'out-of-range': "Valeur hors limites.",
    'unimplemented': "Fonctionnalité non disponible.",
    'internal': "Erreur interne du serveur.",
    'unavailable': "Service temporairement indisponible. Veuillez réessayer.",
    'data-loss': "Erreur de données. Contactez le support.",
    'unauthenticated': "Vous devez être connecté pour effectuer cette action.",
    'cancelled': "Opération annulée.",
    'deadline-exceeded': "Délai d'attente dépassé. Veuillez réessayer.",
    'invalid-argument': "Données invalides.",
};

/**
 * Generic error messages
 */
const GENERIC_ERROR_MESSAGES: Record<string, string> = {
    network: "Erreur de connexion réseau. Vérifiez votre internet.",
    timeout: "Délai d'attente dépassé. Veuillez réessayer.",
    unknown: "Une erreur inattendue s'est produite.",
    validation: "Données invalides. Veuillez vérifier le formulaire.",
    notFound: "Élément introuvable.",
    forbidden: "Action non autorisée.",
    conflict: "Conflit de données. Veuillez rafraîchir la page.",
};

// ============================================================================
// ERROR TYPE DETECTION
// ============================================================================

/**
 * Error types for categorization
 */
export const ERROR_TYPES = {
    AUTH: 'auth',
    FIRESTORE: 'firestore',
    NETWORK: 'network',
    VALIDATION: 'validation',
    UNKNOWN: 'unknown',
} as const;

export type ErrorType = typeof ERROR_TYPES[keyof typeof ERROR_TYPES];

/**
 * Detect the type of error
 * @param error - Error object
 * @returns Error type from ERROR_TYPES
 */
export const getErrorType = (error: AppError | null | undefined): ErrorType => {
    if (!error) return ERROR_TYPES.UNKNOWN;

    const code = (error as FirebaseError).code || '';
    const message = error.message || '';

    // Firebase Auth errors
    if (code.startsWith('auth/')) {
        return ERROR_TYPES.AUTH;
    }

    // Firestore errors
    if (FIRESTORE_ERROR_MESSAGES[code] || code.includes('firestore')) {
        return ERROR_TYPES.FIRESTORE;
    }

    // Network errors
    if (
        message.includes('network') ||
        message.includes('Network') ||
        message.includes('fetch') ||
        message.includes('Failed to fetch') ||
        error.name === 'NetworkError'
    ) {
        return ERROR_TYPES.NETWORK;
    }

    // Validation errors (Zod, form validation)
    if (error.name === 'ZodError' || (error as ZodValidationError).issues) {
        return ERROR_TYPES.VALIDATION;
    }

    return ERROR_TYPES.UNKNOWN;
};

// ============================================================================
// ERROR MESSAGE EXTRACTION
// ============================================================================

/**
 * Handle Firebase Auth errors and return user-friendly message
 * @param error - Firebase Auth error
 * @returns User-friendly error message in French
 */
export const handleAuthError = (error: AppError): string => {
    const code = (error as FirebaseError).code || error.message;
    const message = error.message || '';

    // Check for API key error first (configuration issue)
    if (message.includes('api-key-not-valid') || message.includes('API key not valid')) {
        return AUTH_ERROR_MESSAGES['auth/api-key-not-valid'];
    }

    // Check known error codes
    if (code && AUTH_ERROR_MESSAGES[code]) {
        return AUTH_ERROR_MESSAGES[code];
    }

    // Clean and return generic message
    const cleanMessage = message
        .replace('Firebase: ', '')
        .replace(/Error \(auth\/[^)]+\)\.?/, '')
        .trim();

    return cleanMessage || GENERIC_ERROR_MESSAGES.unknown;
};

/**
 * Handle Firestore errors and return user-friendly message
 * @param error - Firestore error
 * @returns User-friendly error message in French
 */
export const handleFirestoreError = (error: AppError): string => {
    const code = (error as FirebaseError).code || '';

    // Check known Firestore error codes
    if (FIRESTORE_ERROR_MESSAGES[code]) {
        return FIRESTORE_ERROR_MESSAGES[code];
    }

    // Permission denied often has specific message
    if (code === 'permission-denied' || error.message?.includes('permission')) {
        return FIRESTORE_ERROR_MESSAGES['permission-denied'];
    }

    return GENERIC_ERROR_MESSAGES.unknown;
};

/**
 * Get a user-friendly error message from any error type
 * @param error - Error object
 * @returns User-friendly error message in French
 */
export const getErrorMessage = (error: AppError | null | undefined): string => {
    if (!error) return GENERIC_ERROR_MESSAGES.unknown;

    const errorType = getErrorType(error);

    switch (errorType) {
        case ERROR_TYPES.AUTH:
            return handleAuthError(error);
        case ERROR_TYPES.FIRESTORE:
            return handleFirestoreError(error);
        case ERROR_TYPES.NETWORK:
            return GENERIC_ERROR_MESSAGES.network;
        case ERROR_TYPES.VALIDATION:
            // Return first validation error if available
            const zodError = error as ZodValidationError;
            if (zodError.issues && zodError.issues.length > 0) {
                return zodError.issues[0].message;
            }
            return GENERIC_ERROR_MESSAGES.validation;
        default:
            return error.message || GENERIC_ERROR_MESSAGES.unknown;
    }
};

// ============================================================================
// ERROR HANDLING UTILITIES
// ============================================================================

/**
 * Log error with consistent format
 * @param context - Where the error occurred
 * @param error - Error object
 * @param metadata - Additional metadata
 */
export const logError = (context: string, error: AppError, metadata: ErrorMetadata = {}): void => {
    const firebaseError = error as FirebaseError;
    const errorInfo = {
        context,
        type: getErrorType(error),
        code: firebaseError.code,
        message: error.message,
        stack: import.meta.env.DEV ? error.stack : undefined,
        ...metadata,
        timestamp: new Date().toISOString(),
    };

    console.error(`[${context}]`, errorInfo);

    // In production, you might want to send to error tracking service
    // e.g., Sentry, LogRocket, etc.
};

/**
 * Handle error with toast notification
 * @param error - Error object
 * @param options - Options
 * @returns Error message that was displayed
 */
export const handleError = (error: AppError, options: ErrorHandleOptions = {}): string => {
    const {
        context = 'Unknown',
        fallbackMessage = GENERIC_ERROR_MESSAGES.unknown,
        showToast = true,
        log = true,
    } = options;

    const message = getErrorMessage(error) || fallbackMessage;

    if (log) {
        logError(context, error);
    }

    if (showToast) {
        toast.error(message);
    }

    return message;
};

/**
 * Create an async operation wrapper with error handling
 * @param operation - Async operation to wrap
 * @param options - Options for error handling
 * @returns Wrapped operation
 *
 * @example
 * const saveProduct = withErrorHandling(
 *   async (data) => { await updateDoc(...) },
 *   { context: 'ProductForm', fallbackMessage: 'Échec de la sauvegarde' }
 * );
 */
export const withErrorHandling = <TArgs extends unknown[], TResult>(
    operation: (...args: TArgs) => Promise<TResult>,
    options: ErrorHandleOptions = {}
): ((...args: TArgs) => Promise<TResult>) => {
    return async (...args: TArgs): Promise<TResult> => {
        try {
            return await operation(...args);
        } catch (error) {
            handleError(error as AppError, options);
            throw error; // Re-throw for caller to handle if needed
        }
    };
};

/**
 * Try an operation and return result or null on error
 * @param operation - Async operation
 * @param options - Error handling options
 * @returns Result or null
 *
 * @example
 * const user = await tryOperation(
 *   () => getDoc(userRef),
 *   { context: 'UserFetch' }
 * );
 */
export const tryOperation = async <T>(
    operation: () => Promise<T>,
    options: ErrorHandleOptions = {}
): Promise<T | null> => {
    try {
        return await operation();
    } catch (error) {
        handleError(error as AppError, { ...options, showToast: false });
        return null;
    }
};

// ============================================================================
// RETRY LOGIC
// ============================================================================

/**
 * Retry an operation with exponential backoff
 * @param operation - Async operation to retry
 * @param options - Retry options
 * @returns Operation result
 *
 * @example
 * const result = await retryOperation(
 *   () => fetchData(),
 *   { maxRetries: 3, baseDelay: 1000 }
 * );
 */
export const retryOperation = async <T>(
    operation: () => Promise<T>,
    options: RetryOptions = {}
): Promise<T> => {
    const {
        maxRetries = 3,
        baseDelay = 1000,
        shouldRetry = (error: AppError): boolean => {
            // Retry on network errors and temporary failures
            const code = (error as FirebaseError).code || '';
            return (
                getErrorType(error) === ERROR_TYPES.NETWORK ||
                code === 'unavailable' ||
                code === 'resource-exhausted' ||
                code === 'deadline-exceeded'
            );
        },
    } = options;

    let lastError: AppError;

    for (let attempt = 0; attempt <= maxRetries; attempt++) {
        try {
            return await operation();
        } catch (error) {
            lastError = error as AppError;

            if (attempt === maxRetries || !shouldRetry(lastError)) {
                throw error;
            }

            // Exponential backoff with jitter
            const delay = baseDelay * Math.pow(2, attempt) + Math.random() * 1000;
            await new Promise((resolve) => setTimeout(resolve, delay));
        }
    }

    throw lastError!;
};

// ============================================================================
// EXPORTS
// ============================================================================

export {
    AUTH_ERROR_MESSAGES,
    FIRESTORE_ERROR_MESSAGES,
    GENERIC_ERROR_MESSAGES,
};
